/* eslint-disable react/require-default-props */
/**
 * Export Settings Components
 *
 * Presentation components for export configuration using the user settings store.
 * Each component calls useUserSettings() directly to get config from the store.
 */
import React from 'react';
import { ExportFormat, ExportTheme, ExportConfig } from '../../types/models';
import { SettingsInputs } from './SettingsInputs';
import { SettingConfig, SettingValue } from '../../types/settingsTypes';
import { useUserSettings } from '../../stores/settingsRegistry';

interface ExportFormatSelectorProps {
	value: ExportFormat;
	onChange: (format: ExportFormat) => void;
	disabled?: boolean;
	className?: string;
}

interface DocxProfileSelectorProps {
	value: ExportConfig;
	onChange: (config: ExportConfig) => void;
	disabled?: boolean;
	className?: string;
}

interface ExportThemeSelectorProps {
	value: ExportTheme;
	onChange: (theme: ExportTheme) => void;
	disabled?: boolean;
	className?: string;
}

interface ExportConfigSelectorProps {
	value: ExportConfig;
	onChange: (config: ExportConfig) => void;
	disabled?: boolean;
	className?: string;
}

/**
 * Export format selector - uses settings store for config.
 *
 * @example
 * ```tsx
 * <ExportFormatSelector value={format} onChange={setFormat} />
 * ```
 */
export function ExportFormatSelector({
	value,
	onChange,
	disabled = false,
	className = '',
}: ExportFormatSelectorProps) {
	const { helpers } = useUserSettings();
	const setting = helpers.getConfig('export_options.format');

	if (!setting) {
		// eslint-disable-next-line no-console
		console.warn('ExportFormatSelector: export_options.format config not found in store');
		return null;
	}

	return (
		<div className={`flex items-center gap-xs ${className}`}>
			<SettingsInputs.Select
				setting={setting}
				value={value}
				onChange={(v) => onChange(v as ExportFormat)}
				devModeActive={!disabled && setting.editable}
			/>
		</div>
	);
}

/**
 * Export theme selector (dark/light toggle).
 * Uses settings store for config.
 */
export function ExportThemeSelector({
	value,
	onChange,
	disabled = false,
	className = '',
}: ExportThemeSelectorProps) {
	const { helpers } = useUserSettings();
	const setting = helpers.getConfig('export_options.theme');

	if (!setting) {
		// eslint-disable-next-line no-console
		console.warn('ExportThemeSelector: export_options.theme config not found in store');
		return null;
	}

	return (
		<div className={`flex items-center gap-xs ${className}`}>
			<SettingsInputs.Switch
				setting={setting}
				value={value}
				onChange={(v) => onChange(v as ExportTheme)}
				devModeActive={!disabled && setting.editable}
			/>
		</div>
	);
}

/**
 * Export config selector (multi-checkbox for export options).
 * Uses settings store for config.
 *
 * Note: Only renders boolean config options. Non-boolean fields like
 * `imageQuality` are handled separately or ignored for checkbox display.
 */
export function ExportConfigSelector({
	value,
	onChange,
	disabled = false,
	className = '',
}: ExportConfigSelectorProps) {
	const { helpers } = useUserSettings();
	const setting = helpers.getConfig('export_options.config');

	if (!setting) {
		// eslint-disable-next-line no-console
		console.warn('ExportConfigSelector: export_options.config config not found in store');
		return null;
	}

	// Extract only boolean fields for multiCheckbox compatibility
	// ExportConfig has imageQuality: number which doesn't fit Record<string, boolean>
	const booleanOnlyValue: Record<string, boolean> = Object.entries(value)
		.filter(([, val]) => typeof val === 'boolean')
		.reduce((acc, [key, val]) => ({ ...acc, [key]: val }), {});

	const handleChange = (newValue: SettingValue) => {
		// Merge boolean changes back with original value (preserves non-boolean fields)
		onChange({
			...value,
			...(newValue as Record<string, boolean>),
		});
	};

	return (
		<div className={className}>
			<SettingsInputs.MultiCheckboxGrouped
				setting={setting}
				value={booleanOnlyValue}
				onChange={handleChange}
				devModeActive={!disabled && setting.editable}
			/>
		</div>
	);
}

/**
 * DOCX profile selector — typography/layout preset for Word exports.
 *
 * Why: The checkbox group only handles boolean fields; DOCX needs a profile
 * selection to control typography/layout presets (generic vs memo).
 * Should only render when format === 'docx'.
 */
export function DocxProfileSelector({
	value,
	onChange,
	disabled = false,
	className = '',
}: DocxProfileSelectorProps) {
	const current = (value as any).docxProfile ?? 'generic';

	// docxProfile lives inside ExportConfig, not as a standalone client setting,
	// so we provide a local SettingConfig for the shared Select component.
	const profileSetting: SettingConfig = {
		key: 'export_options.config.docxProfile',
		label: 'DOCX Profile',
		description: 'Typography/layout preset for Word exports',
		category: 'Export',
		type: 'string',
		controlType: 'select',
		optionsSource: 'static',
		options: [
			{ value: 'generic', label: 'Generic (Roboto)' },
			{ value: 'memo', label: 'Memo (Times New Roman)' },
		],
		default: 'generic',
		isClientConfig: true,
		editable: !disabled,
	};

	return (
		<div className={`flex items-center gap-xs ${className}`}>
			<SettingsInputs.Select
				setting={profileSetting}
				value={current}
				onChange={(v) =>
					onChange({
						...value,
						docxProfile: v as string,
					} as any)
				}
				devModeActive={!disabled && profileSetting.editable}
			/>
		</div>
	);
}

/**
 * Combined export settings panel with all selectors.
 * Convenience component for showing all export options together.
 */
interface ExportSettingsPanelProps {
	format: ExportFormat;
	theme: ExportTheme;
	config: ExportConfig;
	onFormatChange: (format: ExportFormat) => void;
	onThemeChange: (theme: ExportTheme) => void;
	onConfigChange: (config: ExportConfig) => void;
	disabled?: boolean;
	className?: string;
}

export function ExportSettingsPanel({
	format,
	theme,
	config,
	onFormatChange,
	onThemeChange,
	onConfigChange,
	disabled = false,
	className = '',
}: ExportSettingsPanelProps) {
	const isMagazine = format === 'magazine';

	return (
		<div className={`flex flex-col gap-md ${className}`}>
			<div className="flex items-center justify-between">
				<span className="text-sm text-neutral-200">Format</span>
				<ExportFormatSelector
					value={format}
					onChange={onFormatChange}
					disabled={disabled}
				/>
			</div>
			{!isMagazine && format === 'docx' && (
				<div className="flex items-center justify-between">
					<span className="text-sm text-neutral-200">DOCX Profile</span>
					<DocxProfileSelector
						value={config}
						onChange={onConfigChange}
						disabled={disabled}
					/>
				</div>
			)}
			{!isMagazine && (
			<div className="flex items-center justify-between">
				<span className="text-sm text-neutral-200">Theme</span>
				<ExportThemeSelector
					value={theme}
					onChange={onThemeChange}
					disabled={disabled}
				/>
			</div>
			)}
			{!isMagazine && (
			<div className="flex flex-col gap-xs">
				<span className="text-sm text-neutral-200">Include in Export</span>
				<ExportConfigSelector
					value={config}
					onChange={onConfigChange}
					disabled={disabled}
				/>
			</div>
			)}
		</div>
	);
}
